import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ChatMessage as GlobalChatMessage,
  ChatMessageThought,
  ChatTab,
  ChatContextChip,
  DataBlock,
} from "skedyul-ui/ui";

export type AgentChatSession = {
  conversationId?: string;
  messages: GlobalChatMessage[];
  isStreaming: boolean;
  error?: string | null;
  /** Context chips representing entities touched in this conversation */
  contextChips: ChatContextChip[];
};

export type AgentChatState = {
  activeTabId: string;
  tabs: ChatTab[];
  sessions: Record<string, AgentChatSession>;
};

const makeEmptySession = (): AgentChatSession => ({
  messages: [],
  isStreaming: false,
  error: null,
  contextChips: [],
});

const generateTabId = () =>
  `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createDraftTab = (title: string = "New chat"): ChatTab => ({
  id: generateTabId(),
  title,
  icon: "Bot",
  isDraft: true,
});

const initialTab = createDraftTab();

const initialState: AgentChatState = {
  activeTabId: initialTab.id,
  tabs: [initialTab],
  sessions: {
    [initialTab.id]: makeEmptySession(),
  },
};

export type SetConversationIdPayload = {
  tabId: string;
  conversationId: string;
};

export type AppendMessagePayload = {
  tabId: string;
  message: GlobalChatMessage;
};

export type UpdateAssistantMessagePayload = {
  tabId: string;
  content: string;
  /** Optional thoughts to include with the message */
  thoughts?: ChatMessageThought[];
  /** Optional data blocks from tool results */
  data?: DataBlock[];
};

export type UpdateAssistantDataPayload = {
  tabId: string;
  /** Data blocks to append to the current assistant message */
  data: DataBlock[];
};

export type UpdateAssistantThoughtPayload = {
  tabId: string;
  /** Current thought content */
  thoughtContent: string;
  /** Whether the thought is still being streamed */
  isThinking: boolean;
  /** Duration in ms (set when thought completes) */
  durationMs?: number;
};

export type SetStreamingStatePayload = {
  tabId: string;
  isStreaming: boolean;
};

export type SetErrorPayload = {
  tabId: string;
  error: string | null;
};

export type UpdateContextChipsPayload = {
  tabId: string;
  /** Full replacement of context chips */
  contextChips: ChatContextChip[];
};

export type HydratedConversationPayload = {
  id: string;
  title: string;
  messages: GlobalChatMessage[];
  contextChips?: ChatContextChip[];
};

export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState,
  reducers: {
    hydrateConversations: (
      state,
      action: PayloadAction<HydratedConversationPayload[]>,
    ) => {
      const conversations = action.payload;
      if (!conversations.length) {
        return;
      }

      state.tabs = conversations.map((conversation) => ({
        id: conversation.id,
        title: conversation.title,
        icon: "Bot",
        isDraft: false,
      }));

      state.sessions = conversations.reduce(
        (acc, conversation) => {
          acc[conversation.id] = {
            conversationId: conversation.id,
            messages: conversation.messages,
            isStreaming: false,
            error: null,
            contextChips: conversation.contextChips ?? [],
          };
          return acc;
        },
        {} as Record<string, AgentChatSession>,
      );

      state.activeTabId = conversations[0]?.id ?? state.activeTabId;
    },
    createNewTab: (
      state,
      action: PayloadAction<
        { makeActive?: boolean; title?: string } | undefined
      >,
    ) => {
      const tab = createDraftTab(action?.payload?.title);
      state.tabs.push(tab);
      state.sessions[tab.id] = makeEmptySession();
      if (action?.payload?.makeActive !== false) {
        state.activeTabId = tab.id;
      }
    },
    finalizeConversationTab: (
      state,
      action: PayloadAction<{
        tempId: string;
        conversationId: string;
        title?: string;
      }>,
    ) => {
      const { tempId, conversationId, title } = action.payload;
      if (tempId === conversationId) {
        const tabIndex = state.tabs.findIndex(
          (tab) => tab.id === conversationId,
        );
        if (tabIndex !== -1 && title) {
          const existingTab = state.tabs[tabIndex]!;
          state.tabs[tabIndex] = {
            ...existingTab,
            id: existingTab.id,
            title,
            isDraft: false,
          } as ChatTab;
        }
        const existingSession = state.sessions[conversationId];
        if (existingSession) {
          existingSession.conversationId = conversationId;
        }
        return;
      }

      const session = state.sessions[tempId] ?? makeEmptySession();
      session.conversationId = conversationId;
      delete state.sessions[tempId];
      state.sessions[conversationId] = session;

      state.tabs = state.tabs.map((tab) =>
        tab.id === tempId
          ? {
              ...tab,
              id: conversationId,
              title: title ?? tab.title,
              isDraft: false,
            }
          : tab,
      );

      if (state.activeTabId === tempId) {
        state.activeTabId = conversationId;
      }
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      const tabId = action.payload;
      const tabExists = state.tabs.some((tab) => tab.id === tabId);
      if (!tabExists) {
        state.sessions[tabId] = makeEmptySession();
        state.tabs.push({
          id: tabId,
          title: "New chat",
          icon: "Bot",
          isDraft: true,
        });
      }
      state.activeTabId = tabId;
    },
    renameTab: (
      state,
      action: PayloadAction<{
        tabId: string;
        title: string;
        isDraft?: boolean;
      }>,
    ) => {
      const { tabId, title, isDraft = false } = action.payload;
      state.tabs = state.tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              title,
              isDraft,
            }
          : tab,
      );
    },
    setConversationId: (
      state,
      action: PayloadAction<SetConversationIdPayload>,
    ) => {
      const { tabId, conversationId } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      state.sessions[tabId] = {
        ...session,
        conversationId,
      };
    },
    appendMessage: (state, action: PayloadAction<AppendMessagePayload>) => {
      const { tabId, message } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      state.sessions[tabId] = {
        ...session,
        messages: [...session.messages, message],
      };
    },
    updateAssistantMessage: (
      state,
      action: PayloadAction<UpdateAssistantMessagePayload>,
    ) => {
      const { tabId, content, thoughts, data } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      const messages = [...session.messages];
      const lastIndex = messages.length - 1;

      const lastMessage = lastIndex >= 0 ? messages[lastIndex] : undefined;

      if (lastMessage && lastMessage.author === "assistant") {
        messages[lastIndex] = {
          ...lastMessage,
          content,
          thoughts: thoughts ?? lastMessage.thoughts,
          data: data ?? lastMessage.data,
        };
      } else {
        messages.push({
          id: `assistant-${Date.now().toString()}`,
          author: "assistant",
          sender: "Ops copilot",
          timestamp: "Just now",
          content,
          thoughts,
          data,
        });
      }

      state.sessions[tabId] = {
        ...session,
        messages,
      };
    },
    /**
     * Append data blocks to the current assistant message during streaming.
     */
    updateAssistantData: (
      state,
      action: PayloadAction<UpdateAssistantDataPayload>,
    ) => {
      const { tabId, data } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      const messages = [...session.messages];
      const lastIndex = messages.length - 1;

      let lastMessage = lastIndex >= 0 ? messages[lastIndex] : undefined;

      // Create assistant message if it doesn't exist
      if (!lastMessage || lastMessage.author !== "assistant") {
        lastMessage = {
          id: `assistant-${Date.now().toString()}`,
          author: "assistant",
          sender: "Ops copilot",
          timestamp: "Just now",
          content: "",
          data: [],
        };
        messages.push(lastMessage);
      }

      // Append new data blocks
      const currentData = lastMessage.data ?? [];
      messages[messages.length - 1] = {
        ...lastMessage,
        data: [...currentData, ...data],
      };

      state.sessions[tabId] = {
        ...session,
        messages,
      };
    },
    /**
     * Update or add a thought to the current assistant message during streaming.
     * This handles the "Thinking..." state and completed thoughts.
     */
    updateAssistantThought: (
      state,
      action: PayloadAction<UpdateAssistantThoughtPayload>,
    ) => {
      const { tabId, thoughtContent, isThinking, durationMs } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      const messages = [...session.messages];
      const lastIndex = messages.length - 1;

      let lastMessage = lastIndex >= 0 ? messages[lastIndex] : undefined;

      // Create assistant message if it doesn't exist
      if (!lastMessage || lastMessage.author !== "assistant") {
        lastMessage = {
          id: `assistant-${Date.now().toString()}`,
          author: "assistant",
          sender: "Ops copilot",
          timestamp: "Just now",
          content: "",
          thoughts: [],
        };
        messages.push(lastMessage);
      }

      const currentThoughts = lastMessage.thoughts ?? [];
      const lastThoughtIndex = currentThoughts.length - 1;
      const lastThought =
        lastThoughtIndex >= 0 ? currentThoughts[lastThoughtIndex] : undefined;

      if (isThinking) {
        // Update or add the "thinking" thought
        if (lastThought?.isThinking) {
          // Update existing thinking thought
          currentThoughts[lastThoughtIndex] = {
            ...lastThought,
            content: thoughtContent,
          };
        } else {
          // Add new thinking thought
          currentThoughts.push({
            content: thoughtContent,
            isThinking: true,
          });
        }
      } else {
        // Thought is complete - finalize it
        if (lastThought?.isThinking) {
          currentThoughts[lastThoughtIndex] = {
            content: thoughtContent,
            isThinking: false,
            durationMs,
          };
        } else {
          // Add as completed thought
          currentThoughts.push({
            content: thoughtContent,
            isThinking: false,
            durationMs,
          });
        }
      }

      messages[messages.length - 1] = {
        ...lastMessage,
        thoughts: currentThoughts,
      };

      state.sessions[tabId] = {
        ...session,
        messages,
      };
    },
    setStreamingState: (
      state,
      action: PayloadAction<SetStreamingStatePayload>,
    ) => {
      const { tabId, isStreaming } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      state.sessions[tabId] = {
        ...session,
        isStreaming,
      };
    },
    setError: (state, action: PayloadAction<SetErrorPayload>) => {
      const { tabId, error } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      state.sessions[tabId] = {
        ...session,
        error,
      };
    },
    updateContextChips: (
      state,
      action: PayloadAction<UpdateContextChipsPayload>,
    ) => {
      const { tabId, contextChips } = action.payload;
      const session = state.sessions[tabId] ?? makeEmptySession();
      state.sessions[tabId] = {
        ...session,
        contextChips,
      };
    },
  },
});

export const {
  hydrateConversations,
  createNewTab,
  setActiveTab,
  finalizeConversationTab,
  renameTab,
  setConversationId,
  appendMessage,
  updateAssistantMessage,
  updateAssistantData,
  updateAssistantThought,
  setStreamingState,
  setError,
  updateContextChips,
} = agentChatSlice.actions;

export const agentChatReducer = agentChatSlice.reducer;
